/**
 * Audit Log Service
 * Logs all administrative actions for compliance and security
 */

import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  getDocs,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase';

const AUDIT_LOGS_COLLECTION = 'audit_logs';

export interface AuditLogEntry {
  id?: string;
  timestamp: Timestamp;
  action: string;
  resource: string;
  resourceId?: string;
  performedBy: string;
  performedByEmail?: string;
  performedByName?: string;
  ipAddress?: string;
  userAgent?: string;
  details?: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export interface AuditLogFilters {
  action?: string;
  resource?: string;
  performedBy?: string;
  startDate?: Date;
  endDate?: Date;
  severity?: AuditLogEntry['severity'];
  limit?: number;
}

/**
 * Log an audit event
 */
export const logAuditEvent = async (params: {
  action: string;
  resource: string;
  resourceId?: string;
  details?: Record<string, any>;
  performedBy: string;
  performedByEmail?: string;
  performedByName?: string;
  ipAddress?: string;
  userAgent?: string;
}): Promise<void> => {
  try {
    const severity = getActionSeverity(params.action);

    const logEntry: Omit<AuditLogEntry, 'id'> = {
      timestamp: Timestamp.now(),
      action: params.action,
      resource: params.resource,
      resourceId: params.resourceId,
      performedBy: params.performedBy,
      performedByEmail: params.performedByEmail || 'unknown',
      performedByName: params.performedByName || 'unknown',
      ipAddress: params.ipAddress || 'unknown',
      userAgent: params.userAgent || 'unknown',
      details: params.details || {},
      severity,
    };

    await addDoc(collection(db, AUDIT_LOGS_COLLECTION), logEntry);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Audit Log:', logEntry);
    }
  } catch (error) {
    console.error('Error logging audit event:', error);
    // Don't throw - audit logging should not break the application
  }
};

/**
 * Get audit logs with filters
 */
export const getAuditLogs = async (
  filters: AuditLogFilters = {}
): Promise<AuditLogEntry[]> => {
  try {
    const constraints: QueryConstraint[] = [];

    // Apply filters
    if (filters.action) {
      constraints.push(where('action', '==', filters.action));
    }

    if (filters.resource) {
      constraints.push(where('resource', '==', filters.resource));
    }

    if (filters.performedBy) {
      constraints.push(where('performedBy', '==', filters.performedBy));
    }

    if (filters.severity) {
      constraints.push(where('severity', '==', filters.severity));
    }

    if (filters.startDate) {
      constraints.push(
        where('timestamp', '>=', Timestamp.fromDate(filters.startDate))
      );
    }

    if (filters.endDate) {
      constraints.push(
        where('timestamp', '<=', Timestamp.fromDate(filters.endDate))
      );
    }

    // Sort by timestamp descending
    constraints.push(orderBy('timestamp', 'desc'));

    // Apply limit
    if (filters.limit) {
      constraints.push(firestoreLimit(filters.limit));
    }

    const logsQuery = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      ...constraints
    );
    const snapshot = await getDocs(logsQuery);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as AuditLogEntry[];
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    throw new Error('Failed to fetch audit logs');
  }
};

/**
 * Get recent audit logs for a specific user
 */
export const getUserAuditLogs = async (
  userId: string,
  limit: number = 50
): Promise<AuditLogEntry[]> => {
  return getAuditLogs({
    performedBy: userId,
    limit,
  });
};

/**
 * Get recent audit logs for a specific resource
 */
export const getResourceAuditLogs = async (
  resourceId: string,
  limit: number = 50
): Promise<AuditLogEntry[]> => {
  try {
    const logsQuery = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      where('resourceId', '==', resourceId),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limit)
    );

    const snapshot = await getDocs(logsQuery);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as AuditLogEntry[];
  } catch (error) {
    console.error('Error fetching resource audit logs:', error);
    throw new Error('Failed to fetch resource audit logs');
  }
};

/**
 * Determine severity based on action type
 */
function getActionSeverity(
  action: string
): AuditLogEntry['severity'] {
  const criticalActions = [
    'user.permanent_delete',
    'user.role_change',
    'system.settings_change',
    'system.backup_restore',
    'billing.refund_issue',
  ];

  const highActions = [
    'user.delete',
    'user.suspend',
    'user.role_assign',
    'user.impersonate',
    'system.integration_change',
    'billing.subscription_cancel',
  ];

  const mediumActions = [
    'user.create',
    'user.update',
    'user.activate',
    'billing.invoice_create',
    'support.ticket_close',
    'onboarding.session_create',
  ];

  if (criticalActions.some(a => action.startsWith(a))) {
    return 'critical';
  }

  if (highActions.some(a => action.startsWith(a))) {
    return 'high';
  }

  if (mediumActions.some(a => action.startsWith(a))) {
    return 'medium';
  }

  return 'low';
}

/**
 * Get audit log statistics
 */
export const getAuditLogStats = async (
  startDate?: Date,
  endDate?: Date
): Promise<{
  totalLogs: number;
  bySeverity: Record<AuditLogEntry['severity'], number>;
  byAction: Record<string, number>;
  byUser: Record<string, number>;
}> => {
  try {
    const filters: AuditLogFilters = {};
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;

    const logs = await getAuditLogs(filters);

    const stats = {
      totalLogs: logs.length,
      bySeverity: {
        low: logs.filter(l => l.severity === 'low').length,
        medium: logs.filter(l => l.severity === 'medium').length,
        high: logs.filter(l => l.severity === 'high').length,
        critical: logs.filter(l => l.severity === 'critical').length,
      },
      byAction: logs.reduce((acc, log) => {
        acc[log.action] = (acc[log.action] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      byUser: logs.reduce((acc, log) => {
        acc[log.performedBy] = (acc[log.performedBy] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
    };

    return stats;
  } catch (error) {
    console.error('Error fetching audit log stats:', error);
    throw new Error('Failed to fetch audit log stats');
  }
};
